import { useEffect, useState } from "react";
import type { AttendeeAccountState } from "./useAttendeeAccount";
import { Link } from "./router";
import { DeleteAccountPanel } from "./DeleteAccountPanel";
import { associationPresentation } from "./association-presentation";

/**
 * Her account, and only that.
 *
 * This screen used to carry the whole signed-out sign-in flow as well — an email form, a
 * six-digit code box, and a "not registered" notice. The signed-out gate in App sends
 * everybody to the join form before this renders, so all of it was unreachable, and the
 * mailed code it asked for no longer exists.
 */

/**
 * The way through to the staff console, for the people who have one.
 *
 * The staff side has been able to open the guide since 27 August and this side could not
 * go back, so one sign-in covered both doors in one direction only. A `__Host-` cookie
 * cannot cross between `wvcea.app` and `staff.wvcea.app` — different hosts, by design —
 * so a one-use token makes the trip and the cookie is set by the host that will send it.
 *
 * Asked before it is drawn, so a teacher is never shown a door that would refuse her.
 */
function StaffConsoleCrossing() {
  const [available, setAvailable] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void fetch("/api/attendee/staff-handoff", { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() as Promise<{ available?: boolean }> : null))
      .then((body) => { if (live && body?.available) setAvailable(true); })
      .catch(() => undefined);
    return () => { live = false; };
  }, []);

  if (!available) return null;

  const cross = async () => {
    setFailed(false);
    try {
      const response = await fetch("/api/attendee/staff-handoff", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error("no handoff");
      const body = await response.json() as { url: string };
      window.location.assign(body.url);
    } catch {
      // Deliberately does not fall back to the plain staff address: arriving there signed
      // out is the second sign-in this exists to remove, and saying so is more honest.
      setFailed(true);
    }
  };

  return (
    <section className="attendee-account-record" aria-labelledby="account-staff-title">
      <div><span aria-hidden="true">→</span><div>
        <h2 id="account-staff-title">Staff console</h2>
        <p>You hold staff access. Open the console without signing in again.</p>
        {failed && <p role="alert">That did not open. Try again in a moment.</p>}
      </div></div>
      <button type="button" className="primary-action" onClick={() => void cross()}>Open staff console</button>
    </section>
  );
}

export function AttendeeAccountPage({ state, eventSlug, signOut }: {
  state: AttendeeAccountState;
  eventSlug: string;
  signOut: () => Promise<void>;
}) {
  const presentation = associationPresentation();
  if (state.status === "loading") return <div className="attendee-account-loading" aria-busy="true" aria-label="Loading your account"><span /><span /><span /></div>;
  if (state.status === "signed-in") return (
    <div className="page attendee-account-page">
      {/* Reached from More, and until now there was no way back to it. In a browser that
          is the back gesture's job. In the installed application each tab is its own web
          view, so a tab left on this page stays here — through tab switches, sign-out and
          relaunch. Same trap as the Convention Wrap-Up had. */}
      <Link className="back-link subpage-back" href="/more">← More</Link>
      <header className="attendee-account-heading">
        <p>{presentation.appName} account</p>
        <h1 data-page-heading tabIndex={-1}>{state.session.displayName}</h1>
        <span>{state.session.email}</span>
        {state.session.eventAccess[0]?.schoolName && <span>{state.session.eventAccess[0].schoolName}</span>}
      </header>

      <section className="attendee-account-record" aria-labelledby="account-event-title">
        <div><span aria-hidden="true">✓</span><div><h2 id="account-event-title">{presentation.shortName} account connected</h2><p>Your messages, events, records, schedules, and notes follow you onto any device you sign in on.</p></div></div>
        <Link className="primary-action" href="/">Open your home</Link>
      </section>

      <StaffConsoleCrossing />

      <button className="attendee-signout" type="button" onClick={() => void signOut()}>Sign out on this device</button>

      {/* Last on the page and behind two taps. Nothing above it is irreversible and this
          is, so it does not sit among the things somebody came here to do. */}
      <DeleteAccountPanel
        eventSlug={eventSlug}
        identityId={state.session.identityId}
        onDeleted={(signedOut) => {
          // A teacher who kept her sign-in because she also runs a school stays where she
          // is; everyone else has nothing left to be signed in to.
          if (signedOut) void signOut();
          else window.location.assign("/");
        }}
      />
    </div>
  );
}
